import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Activity, RefreshCw, Circle, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { useBullmqCounts, type QueueCounts } from '@/hooks/useBullmqCounts'

// Empty-string fallback lets prod requests go same-origin (Express serves /health at
// root); dev still supplies VITE_API_URL=http://localhost:3002 via apps/web/.env.
// Matches the pattern adopted for API_BASE_URL in apps/web/src/lib/api.ts.
const API_BASE = import.meta.env.VITE_API_URL ?? ''

// ─── Types ────────────────────────────────────────────────────────────────────

type ServiceStatus = 'Operational' | 'Connected' | 'Running' | 'Degraded' | 'Down' | 'Unknown'
type LogLevel = 'ERROR' | 'WARN' | 'INFO'

interface ServiceCard {
  label: string
  status: ServiceStatus
  live: boolean
}

interface QueueRow extends QueueCounts {
  name: string
}

interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
  source: string
}

// ─── Mock data ────────────────────────────────────────────────────────────────
// Queue stats are now live-fetched via useBullmqCounts — see QueueStats below.
// Logs remain mocked pending a dedicated log-feed endpoint.

const MOCK_LOGS: LogEntry[] = [
  {
    timestamp: '2026-04-08 06:14:02',
    level: 'ERROR',
    message: 'Failed to fetch feed: connection timeout after 30s',
    source: 'pipeline/ingest',
  },
  {
    timestamp: '2026-04-08 06:13:47',
    level: 'WARN',
    message: 'AI provider rate limit approaching (85% of quota)',
    source: 'pipeline/classify',
  },
  {
    timestamp: '2026-04-08 06:12:30',
    level: 'INFO',
    message: 'Daily briefing dispatched to 47 subscribers',
    source: 'email/briefing',
  },
  {
    timestamp: '2026-04-08 06:10:05',
    level: 'WARN',
    message: 'Duplicate article detected and skipped: CVE-2025-1234',
    source: 'pipeline/dedup',
  },
  {
    timestamp: '2026-04-08 05:58:11',
    level: 'ERROR',
    message: 'Resend webhook signature verification failed for event msg_0x…',
    source: 'api/webhooks',
  },
  {
    timestamp: '2026-04-08 05:45:00',
    level: 'INFO',
    message: 'Briefing scheduler triggered (UTC 05:30)',
    source: 'scheduler',
  },
  {
    timestamp: '2026-04-08 05:32:18',
    level: 'WARN',
    message: 'Vendor name resolution failed for "CyberArk Inc." — no match in registry',
    source: 'pipeline/link',
  },
  {
    timestamp: '2026-04-08 04:01:44',
    level: 'INFO',
    message: 'Seed ingest complete: 23 articles queued for processing',
    source: 'pipeline/ingest',
  },
]

const LEVEL_BADGE: Record<LogLevel, string> = {
  ERROR: 'bg-red-500/15 text-red-600 border-red-200',
  WARN:  'bg-yellow-500/15 text-yellow-700 border-yellow-200',
  INFO:  'bg-blue-500/15 text-blue-600 border-blue-200',
}

// ─── Status dot ───────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: ServiceStatus }) {
  const colour =
    status === 'Operational' || status === 'Connected' || status === 'Running'
      ? 'text-green-500'
      : status === 'Degraded'
      ? 'text-yellow-500'
      : 'text-red-500'

  return <Circle className={`h-3 w-3 fill-current ${colour}`} />
}

// ─── Status overview cards ────────────────────────────────────────────────────

interface StatusOverviewProps {
  apiStatus: ServiceStatus
}

function StatusOverview({ apiStatus }: StatusOverviewProps) {
  const services: ServiceCard[] = [
    { label: 'API Server', status: apiStatus,      live: true  },
    { label: 'Database',   status: 'Connected',    live: false },
    { label: 'Redis',      status: 'Connected',    live: false },
    { label: 'Pipeline',   status: 'Running',      live: false },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {services.map(({ label, status, live }) => (
        <Card key={label}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">{label}</p>
              {!live && (
                <span className="text-xs text-muted-foreground italic">mock</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <StatusDot status={status} />
              <span className="font-semibold text-sm">{status}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ─── Queue stats card ─────────────────────────────────────────────────────────

function QueueStats() {
  const { data, isLoading, isError, error, refetch } = useBullmqCounts()

  // Preserve a stable queue ordering that matches the server-side
  // BULLMQ_QUEUE_NAMES list so the table doesn't jitter between renders.
  const rows: QueueRow[] = data
    ? Object.entries(data.queues).map(([name, counts]) => ({ name, ...counts }))
    : []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pipeline Queue Stats</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isError ? (
          <ErrorState
            title="Couldn't load queue stats"
            message={error instanceof Error ? error.message : 'Unknown error'}
            onRetry={() => refetch()}
          />
        ) : isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/20 text-muted-foreground text-left">
                  <th className="px-4 py-3 font-medium">Queue Name</th>
                  <th className="px-4 py-3 text-right font-medium">Waiting</th>
                  <th className="px-4 py-3 text-right font-medium">Active</th>
                  <th className="px-4 py-3 text-right font-medium">Delayed</th>
                  <th className="px-4 py-3 text-right font-medium">Completed</th>
                  <th className="px-4 py-3 text-right font-medium">Failed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.name} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-xs">
                      <div className="flex items-center gap-1.5">
                        <span>{row.name}</span>
                        {row.error && (
                          <span
                            title={row.error}
                            className="inline-flex items-center text-amber-600"
                            aria-label={`Error fetching counts: ${row.error}`}
                          >
                            <AlertCircle className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{row.waiting}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {row.active > 0 ? (
                        <span className="text-green-600 font-medium">{row.active}</span>
                      ) : row.active}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                      {row.delayed}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                      {row.completed.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {row.failed > 0 ? (
                        <span className="text-red-600 font-medium">{row.failed}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Recent errors card ───────────────────────────────────────────────────────

function RecentErrors() {
  const [search, setSearch] = useState('')

  const filtered = MOCK_LOGS.filter((l) =>
    l.message.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">Recent Errors &amp; Warnings</CardTitle>
          <Input
            placeholder="Filter by message…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-64 text-sm"
          />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/20 text-muted-foreground text-left">
                <th className="px-4 py-3 font-medium whitespace-nowrap">Timestamp</th>
                <th className="px-4 py-3 font-medium">Level</th>
                <th className="px-4 py-3 font-medium">Message</th>
                <th className="px-4 py-3 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                    No log entries match the filter.
                  </td>
                </tr>
              ) : (
                filtered.map((log, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap font-mono">
                      {log.timestamp}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={LEVEL_BADGE[log.level]}>
                        {log.level}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs max-w-[400px]">{log.message}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono whitespace-nowrap">
                      {log.source}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SystemHealthPage() {
  const [lastUpdated, setLastUpdated] = useState(0) // seconds ago

  const { data: apiHealth, refetch } = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/health`)
      return r.json() as Promise<{ status: string }>
    },
    refetchInterval: 30000,
  })

  const apiStatus: ServiceStatus =
    apiHealth?.status === 'ok' || apiHealth?.status === 'healthy'
      ? 'Operational'
      : apiHealth
      ? 'Degraded'
      : 'Unknown'

  // Tick the "last updated" counter
  useEffect(() => {
    setLastUpdated(0)
    const interval = setInterval(() => {
      setLastUpdated((s) => s + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [apiHealth])

  const handleRefresh = useCallback(() => {
    setLastUpdated(0)
    refetch()
  }, [refetch])

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">System Health</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Monitor pipeline, database, and infrastructure status
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Last updated: {lastUpdated}s ago
          </span>
          <Button size="sm" variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status overview */}
      <StatusOverview apiStatus={apiStatus} />

      {/* Queue stats */}
      <QueueStats />

      {/* Recent errors */}
      <RecentErrors />
    </div>
  )
}
